import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import BreadcrumbTrail from '../../components/ui/BreadcrumbTrail';
import Icon from '../../components/AppIcon';
import Image from '../../components/AppImage';
import { ActivityItem } from '../admin-dashboard/types/index';

const ActivityPage = () => {
  const { user, userProfile } = useAuth();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [activities, setActivities] = useState<ActivityItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const isSuperAdmin = userProfile?.role === 'super_admin';

  const getActivityIcon = (type: ActivityItem['type']) => {
    switch (type) {
      case 'contribution':
        return 'DollarSign';
      case 'member':
        return 'UserPlus';
      case 'verification':
        return 'CheckCircle';
      case 'system':
        return 'Bell';
      default:
        return 'Activity';
    }
  };

  const getActivityColor = (type: ActivityItem['type']) => {
    switch (type) {
      case 'contribution':
        return 'var(--color-success)';
      case 'member':
        return 'var(--color-primary)';
      case 'verification':
        return 'var(--color-warning)';
      case 'system':
        return 'var(--color-accent)';
      default:
        return 'var(--color-muted-foreground)';
    }
  };

  const formatTimestamp = (date: Date) => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  useEffect(() => {
    if (!user || !userProfile) return;

    const fetchActivities = async () => {
      try {
        setLoading(true);
        setError(null);

        if (isSuperAdmin) {
          // Super admin: Fetch all contributions from all groups
          const { data: allContributions, error: contributionsError } = await supabase
            .from('contributions')
            .select(`
              id,
              amount,
              status,
              created_at,
              member:user_profiles!contributions_member_id_fkey(id, full_name, email),
              group:groups!contributions_group_id_fkey(id, name)
            `)
            .order('created_at', { ascending: false })
            .limit(100);

          if (contributionsError) throw contributionsError;

          const recentActivities: ActivityItem[] = (allContributions || []).map((c: any) => ({
            id: c.id,
            type: 'contribution',
            title: `${c.member?.full_name || 'Member'} contributed ${c.amount} VT`,
            description: `Group: ${c.group?.name || 'Unknown'}`,
            timestamp: new Date(c.created_at),
            user: {
              avatar: '/assets/images/no-image.jpg',
              alt: c.member?.full_name || 'Member'
            },
            status: c.status === 'approved' ? 'approved' : c.status === 'rejected' ? 'rejected' : 'pending'
          }));

          setActivities(recentActivities);
        } else {
          // Regular admin: Fetch contributions from their groups
          let userGroupIds: string[] = [];
          
          // Get groups where user is admin or creator
          const { data: adminGroups } = await supabase
            .from('group_members')
            .select('group_id')
            .eq('user_id', user.id)
            .in('role', ['admin', 'clerk']);

          const { data: createdGroups } = await supabase
            .from('groups')
            .select('id')
            .eq('created_by', user.id);

          const groupIds = new Set<string>();
          if (adminGroups) {
            adminGroups.forEach(g => groupIds.add(g.group_id));
          }
          if (createdGroups) {
            createdGroups.forEach(g => groupIds.add(g.id));
          }
          userGroupIds = Array.from(groupIds);

          if (userGroupIds.length === 0) {
            setActivities([]);
            setLoading(false);
            return;
          }

          const { data: recentContributions, error: contributionsError } = await supabase
            .from('contributions')
            .select(`
              id,
              amount,
              status,
              created_at,
              member:user_profiles!contributions_member_id_fkey(id, full_name, email),
              group:groups!contributions_group_id_fkey(id, name)
            `)
            .in('group_id', userGroupIds)
            .order('created_at', { ascending: false })
            .limit(100);

          if (contributionsError) throw contributionsError;

          const recentActivities: ActivityItem[] = (recentContributions || []).map(c => ({
            id: c.id,
            type: 'contribution',
            title: `${c.member?.full_name || 'Member'} contributed ${c.amount} VT`,
            description: `Group: ${c.group?.name || 'Unknown'}`,
            timestamp: new Date(c.created_at),
            user: {
              avatar: '/assets/images/no-image.jpg',
              alt: c.member?.full_name || 'Member'
            },
            status: c.status === 'approved' ? 'approved' : c.status === 'rejected' ? 'rejected' : 'pending'
          }));

          setActivities(recentActivities);
        }
      } catch (err: any) {
        console.error('Error fetching activities:', err);
        setError(err.message || 'Failed to load activities');
      } finally {
        setLoading(false);
      }
    };

    fetchActivities();
  }, [user, userProfile, isSuperAdmin]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading activities...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Activity - Nasuman</title>
        <meta name="description" content="View all activity and contributions across your groups" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-emerald-50">
        <Header
          onMenuToggle={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          isMobileMenuOpen={isMobileMenuOpen}
        />

        <div className="flex">
          <div className="hidden lg:block">
            <Sidebar
              isCollapsed={isSidebarCollapsed}
              userRole="admin"
              isSuperAdmin={isSuperAdmin}
            />
          </div>

          <main
            className={`flex-1 transition-layout ${
              isSidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'
            }`}
          >
            <div className="p-4 lg:p-6 max-w-7xl mx-auto">
              <div className="mb-6">
                <BreadcrumbTrail />
              </div>

              {/* Header */}
              <div className="mb-8">
                <div className="flex items-center justify-between">
                  <div>
                    <h1 className="text-3xl lg:text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-emerald-600">
                      Activity
                    </h1>
                    <p className="text-slate-600 mt-1">
                      View all contributions and activities across {isSuperAdmin ? 'all groups' : 'your groups'}
                    </p>
                  </div>
                  <button
                    onClick={() => navigate('/admin-dashboard')}
                    className="px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                  >
                    Back to Dashboard
                  </button>
                </div>
              </div>

              {/* Error State */}
              {error && (
                <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-md">
                  <p className="text-sm text-destructive">{error}</p>
                </div>
              )}

              {/* Activities List */}
              <div className="bg-card border border-border rounded-md shadow-elevation-1">
                <div className="p-6 border-b border-border">
                  <h2 className="text-lg font-semibold text-foreground">
                    All Activities ({activities.length})
                  </h2>
                </div>
                <div className="divide-y divide-border max-h-[calc(100vh-300px)] overflow-y-auto">
                  {activities.length === 0 ? (
                    <div className="p-12 text-center">
                      <Icon name="Activity" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4 opacity-50" />
                      <p className="text-muted-foreground">No activities found</p>
                    </div>
                  ) : (
                    activities.map((activity) => (
                      <div key={activity.id} className="p-4 hover:bg-muted transition-interactive">
                        <div className="flex gap-4">
                          <div
                            className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                            style={{ backgroundColor: `${getActivityColor(activity.type)}15` }}
                          >
                            <Icon
                              name={getActivityIcon(activity.type)}
                              size={18}
                              color={getActivityColor(activity.type)}
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2 mb-1">
                              <div className="flex items-center gap-2">
                                <Image
                                  src={activity.user.avatar}
                                  alt={activity.user.alt}
                                  className="w-6 h-6 rounded-full object-cover"
                                />
                                <h3 className="text-sm font-medium text-foreground">
                                  {activity.title}
                                </h3>
                              </div>
                              <span className="text-xs text-muted-foreground flex-shrink-0">
                                {formatTimestamp(activity.timestamp)}
                              </span>
                            </div>
                            <p className="text-sm text-muted-foreground mb-2">
                              {activity.description}
                            </p>
                            {activity.status && (
                              <span
                                className={`inline-flex items-center px-2 py-1 rounded-sm text-xs font-medium ${
                                  activity.status === 'pending'
                                    ? 'bg-warning/10 text-warning'
                                    : activity.status === 'approved'
                                    ? 'bg-success/10 text-success'
                                    : 'bg-destructive/10 text-destructive'
                                }`}
                              >
                                {activity.status.charAt(0).toUpperCase() + activity.status.slice(1)}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>
    </>
  );
};

export default ActivityPage;
